import { useCallback, useEffect, useMemo, useState } from "react";

import { Alert } from "@/components/alert/alert";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { PokemonSkillCard } from "@/components/pokemon/pokemon-skill-card";
import { Button } from "@/components/ui/buttons/button";
import { useBackAction } from "@/hooks/use-back-action";
import { useAuth } from "@/providers/auth-provider";
import { useSPAContext } from "@/providers/spa-provider";
import { listInventoryPokemons } from "@/services/inventory/list-inventory-pokemons";
import { listPokeTeams } from "@/services/poketeam/list-poketeams";
import { startTrainingBattle } from "@/services/training/start-training-battle";
import { RouteNames } from "@/types/route-names";
import type { PokeTeam } from "@/types";

import { POKE_DATA } from "game/data/pokemon/data";
import { POKEMON_SKILLS } from "game/data/skills/POKEMON_SKILLS";
import type { PokeNames } from "types/pokemon/poke-names";
import type { InventoryPokemonsOutput } from "types/web-app";

import { DojoBattleView } from "./dojo-battle-view";
import { readDojoTrainingConfig } from "./dojo-training-config-view";

type Slot = {
  base_pokemon_name: string | null;
  active_skills: string[];
  owned_pokemon_id?: number | null;
};

function getMoveOptions(baseName: string | null): string[] {
  if (!baseName) return [];
  const data = POKE_DATA[baseName as PokeNames];
  if (!data) return [];
  return [...data.moves];
}

function getDefaultSkills(baseName: string | null): string[] {
  const moves = getMoveOptions(baseName);
  return moves.slice(0, 3);
}

function spritePokemon(baseName: string) {
  return { base_pokemon_name: baseName, is_shiny: false };
}

export const DojoCustomSetupView = () => {
  const { setCurrentRoute, previousRoute } = useSPAContext();
  const { getAccessToken } = useAuth();

  const config = readDojoTrainingConfig();
  const picksRequired = config.format === "1v1" ? 1 : 3;

  const [query, setQuery] = useState("");
  const [isStarting, setIsStarting] = useState(false);
  const [battleData, setBattleData] = useState<{
    battleLog: any;
    botName: string;
    botCountry: string | null;
  } | null>(null);

  const [teamA, setTeamA] = useState<Slot[]>(
    () =>
      Array.from({ length: picksRequired }, () => ({
        base_pokemon_name: null,
        active_skills: [],
      })) as Slot[],
  );
  const [teamB, setTeamB] = useState<Slot[]>(
    () =>
      Array.from({ length: picksRequired }, () => ({
        base_pokemon_name: null,
        active_skills: [],
      })) as Slot[],
  );

  const [activeSide, setActiveSide] = useState<"A" | "B">("A");
  const [activeIndex, setActiveIndex] = useState(0);
  const [ownedPokemons, setOwnedPokemons] = useState<
    InventoryPokemonsOutput["player_pokemons"] | null
  >(null);
  const [isLoadingOwned, setIsLoadingOwned] = useState(false);
  const [teams, setTeams] = useState<PokeTeam[] | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<number | "all">("all");

  const handleBack = useCallback(() => {
    if (battleData) {
      setBattleData(null);
      return;
    }
    setCurrentRoute(previousRoute ?? RouteNames.DOJO_TRAINING_CONFIG);
  }, [battleData, previousRoute, setCurrentRoute]);

  useBackAction(handleBack);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const token = getAccessToken();
      if (!token) return;
      setIsLoadingOwned(true);
      try {
        const res = await listInventoryPokemons(token);
        if (!cancelled) {
          setOwnedPokemons(res.ok && res.data ? res.data.player_pokemons : []);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setOwnedPokemons([]);
      } finally {
        if (!cancelled) setIsLoadingOwned(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [getAccessToken]);

  useEffect(() => {
    let cancelled = false;
    const loadTeams = async () => {
      const token = getAccessToken();
      if (!token) return;
      try {
        const res = await listPokeTeams(token);
        if (!cancelled) {
          setTeams(res.ok && res.data?.teams ? (res.data.teams as any) : []);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setTeams([]);
      }
    };
    void loadTeams();
    return () => {
      cancelled = true;
    };
  }, [getAccessToken]);

  const selectedTeam = useMemo(() => {
    if (!teams || selectedTeamId === "all") return null;
    return teams.find((t) => t.id === selectedTeamId) ?? null;
  }, [teams, selectedTeamId]);

  const orderedTeamSlots = useMemo(() => {
    if (!selectedTeam) return [];
    const order = (selectedTeam.order ?? {}) as Record<string, number>;
    return Array.from({ length: 8 }).map((_, idx) => {
      const pokeId = order[idx.toString()];
      if (!pokeId) return null;
      return selectedTeam.pokemons.find((p) => p.id === pokeId) ?? null;
    });
  }, [selectedTeam]);

  const allNames = useMemo(() => Object.keys(POKE_DATA) as PokeNames[], []);
  const filteredCpu = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? allNames.filter((n) => n.toLowerCase().includes(q))
      : allNames;
    return base.slice(0, 60);
  }, [allNames, query]);

  const filteredOwned = useMemo(() => {
    const list = ownedPokemons ?? [];
    const q = query.trim().toLowerCase();
    const base = q
      ? list.filter((p) =>
          (p.name ?? p.base_pokemon_name).toLowerCase().includes(q),
        )
      : list;
    return base.slice(0, 60);
  }, [ownedPokemons, query]);

  const setSlotPokemon = useCallback(
    (
      baseName: string,
      ownedPokemonId?: number | null,
      ownedSkills?: string[],
    ) => {
      const update = (slots: Slot[]) =>
        slots.map((s, i) =>
          i === activeIndex
            ? {
                base_pokemon_name: baseName,
                owned_pokemon_id: ownedPokemonId ?? null,
                active_skills: (ownedSkills?.length
                  ? ownedSkills
                  : getDefaultSkills(baseName)
                ).map((x) => x.replace(/_/g, "-")),
              }
            : s,
        );
      if (activeSide === "A") setTeamA((prev) => update(prev));
      else setTeamB((prev) => update(prev));
    },
    [activeIndex, activeSide],
  );

  const toggleSkill = useCallback(
    (skill: string) => {
      const update = (slots: Slot[]) =>
        slots.map((s, i) => {
          if (i !== activeIndex) return s;
          const normalized = skill.replace(/_/g, "-");
          const has = s.active_skills.includes(normalized);
          const next = has
            ? s.active_skills.filter((x) => x !== normalized)
            : [...s.active_skills, normalized];
          // cap at 3
          return { ...s, active_skills: next.slice(0, 3) };
        });
      if (activeSide === "A") setTeamA((prev) => update(prev));
      else setTeamB((prev) => update(prev));
    },
    [activeIndex, activeSide],
  );

  const currentSlot =
    activeSide === "A" ? teamA[activeIndex] : teamB[activeIndex];

  const knownSkills = useMemo(() => {
    if (!currentSlot?.base_pokemon_name) return [];
    // If this is a player's owned pokemon, prefer its known_skills list (like pokemon-info)
    if (activeSide === "A" && currentSlot.owned_pokemon_id && ownedPokemons) {
      const owned = ownedPokemons.find(
        (p) => p.id === currentSlot.owned_pokemon_id,
      );
      const known = owned?.known_skills ?? [];
      return known
        .map(
          (id) =>
            POKEMON_SKILLS[
              id.replace(/_/g, "-") as keyof typeof POKEMON_SKILLS
            ],
        )
        .filter(Boolean);
    }
    // Otherwise for CPU/custom templates, use POKE_DATA moves
    return getMoveOptions(currentSlot.base_pokemon_name)
      .map((id) => POKEMON_SKILLS[id as keyof typeof POKEMON_SKILLS])
      .filter(Boolean);
  }, [currentSlot?.base_pokemon_name]);

  const canStart = useMemo(() => {
    const okSlots = (slots: Slot[]) =>
      slots.every((s) => !!s.base_pokemon_name && s.active_skills.length > 0);
    return okSlots(teamA) && okSlots(teamB);
  }, [teamA, teamB]);

  const handleStart = useCallback(async () => {
    if (!canStart) {
      Alert("Pick pokemons and at least 1 skill for each slot");
      return;
    }
    const accessToken = getAccessToken();
    if (!accessToken) {
      Alert("Authentication error. Please try again");
      return;
    }
    setIsStarting(true);
    try {
      const response = await startTrainingBattle(accessToken, {
        pokemon_ids: [], // ignored in custom
        mode: "quick_training",
        format: config.format,
        cpu_type: "custom",
        custom: {
          teamA: teamA.map((s) => ({
            base_pokemon_name: s.base_pokemon_name!,
            active_skills: s.active_skills,
          })),
          teamB: teamB.map((s) => ({
            base_pokemon_name: s.base_pokemon_name!,
            active_skills: s.active_skills,
          })),
        },
      });

      if (response.ok && response.data) {
        setBattleData({
          battleLog: response.data.battle_log,
          botName: response.data.bot_name,
          botCountry: response.data.bot_country,
        });
      } else {
        Alert(response.message || "Failed to start training battle");
      }
    } catch (e) {
      console.error(e);
      Alert("Failed to start training battle");
    } finally {
      setIsStarting(false);
    }
  }, [canStart, config.format, getAccessToken, teamA, teamB]);

  if (battleData) {
    return (
      <DojoBattleView
        battleLog={battleData.battleLog}
        botName={battleData.botName}
        botCountry={battleData.botCountry}
        onBack={handleBack}
        onRematch={handleStart}
      />
    );
  }

  return (
    <div className="relative flex flex-col bg-pk-night-1/90 backdrop-blur-md pb-24 w-full h-full overflow-y-auto">
      <div className="z-10 relative flex justify-between items-center mt-6 xs:mt-8">
        <div className="flex flex-col ml-4 p-2">
          <div className="relative">
            <h1 className="drop-shadow-lg font-black text-white text-3xl xs:text-4xl uppercase leading-none tracking-tight">
              CUSTOM
            </h1>
            <div className="bg-pk-red-1 mt-1 w-12 xs:w-16 h-1 skew-x-[-20deg]" />
          </div>
          <p className="mt-2 text-pk-gray-4 text-sm">
            {config.format} • Level 50 • Pick {picksRequired} vs {picksRequired}
          </p>
        </div>
        <button
          onClick={handleBack}
          className="mr-4 px-4 py-2 text-pk-gray-4 hover:text-white transition-colors"
        >
          ← Back
        </button>
      </div>

      <div className="z-10 relative flex flex-col gap-4 mx-auto px-4 pt-4 w-full max-w-md">
        {/* Slot selector */}
        <div className="bg-black/20 border border-white/10 rounded-xl p-3">
          <div className="flex gap-2 mb-3">
            <Button
              variant={activeSide === "A" ? "red" : "night"}
              className="flex-1"
              onClick={() => setActiveSide("A")}
            >
              You
            </Button>
            <Button
              variant={activeSide === "B" ? "red" : "night"}
              className="flex-1"
              onClick={() => setActiveSide("B")}
            >
              CPU
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {Array.from({ length: picksRequired }).map((_, i) => {
              const slot = (activeSide === "A" ? teamA : teamB)[i];
              const label = slot?.base_pokemon_name ?? `Slot ${i + 1}`;
              const isActive = i === activeIndex;
              return (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={`rounded-lg border px-2 py-2 text-left text-xs transition-colors ${
                    isActive
                      ? "border-white/30 bg-pk-night-2 text-white"
                      : "border-white/10 bg-black/10 text-pk-gray-4"
                  }`}
                >
                  <div className="font-bold truncate">{label}</div>
                  <div className="opacity-70">
                    {slot?.active_skills.length ?? 0}/3 skills
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Search */}
        <div className="bg-black/20 border border-white/10 rounded-xl p-3">
          {/* Quick pick from team (You side only) */}
          {activeSide === "A" && teams && teams.length > 0 && (
            <div className="mb-3">
              <div className="flex items-center justify-between gap-2">
                <div className="text-pk-gray-4 text-xs uppercase tracking-widest font-bold">
                  Pick from team
                </div>
                <select
                  value={selectedTeamId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedTeamId(v === "all" ? "all" : Number(v));
                  }}
                  className="bg-black/30 border border-white/10 rounded-md px-2 py-1 text-white text-xs"
                >
                  <option value="all">All (inventory)</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              {selectedTeam && (
                <div className="mt-2 grid grid-cols-4 gap-2">
                  {orderedTeamSlots.map((p, idx) => (
                    <button
                      key={p ? p.id : `empty-${idx}`}
                      disabled={!p}
                      onClick={() => {
                        if (!p) return;
                        // Find full pokemon from inventory to use its active/known skills
                        const full =
                          ownedPokemons?.find((x) => x.id === p.id) ?? null;
                        setSlotPokemon(
                          p.base_pokemon_name,
                          p.id,
                          full?.active_skills ?? [],
                        );
                      }}
                      className={`relative rounded-lg border border-white/10 bg-black/20 h-16 flex items-center justify-center ${
                        p ? "hover:bg-pk-night-2/60" : "opacity-30"
                      }`}
                    >
                      {p ? (
                        <PokemonSprite
                          pokemon={p}
                          className="w-12 h-12"
                          style={{ scale: 1.35 }}
                          ignoreScale={true}
                        />
                      ) : (
                        <span className="text-pk-gray-4">—</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pokemon by name..."
            className="w-full rounded-lg bg-black/30 border border-white/10 px-3 py-2 text-white outline-none"
          />

          <div className="mt-3 max-h-[240px] overflow-y-auto grid grid-cols-2 gap-2">
            {activeSide === "A" ? (
              <>
                {isLoadingOwned && (
                  <div className="col-span-2 text-pk-gray-4 text-sm italic py-2">
                    Loading your Pokémon...
                  </div>
                )}
                {!isLoadingOwned &&
                  (selectedTeamId === "all"
                    ? filteredOwned
                    : filteredOwned.filter(
                        (p) =>
                          selectedTeam?.pokemons.some((tp) => tp.id === p.id),
                      )
                  ).map((p) => {
                    const label = p.name ?? p.base_pokemon_name;
                    const isSelected =
                      currentSlot?.owned_pokemon_id === p.id ||
                      currentSlot?.base_pokemon_name === p.base_pokemon_name;
                    return (
                      <button
                        key={p.id}
                        onClick={() =>
                          setSlotPokemon(
                            p.base_pokemon_name,
                            p.id,
                            p.active_skills,
                          )
                        }
                        className="relative rounded-lg bg-pk-night-2/60 hover:bg-pk-night-2 border border-white/10 px-2 py-2 text-left text-white text-sm overflow-hidden"
                      >
                        {isSelected && (
                          <div className="z-10 absolute top-1 right-1 rounded-md px-2 py-0.5 bg-pk-green-3 text-[10px] text-white font-semibold shadow-lg">
                            SELECTED
                          </div>
                        )}

                        <div className="flex items-center gap-2">
                          <div className="w-12 h-12 rounded-md bg-black/20 border border-white/10 flex items-center justify-center">
                            <PokemonSprite
                              pokemon={p}
                              className="w-10 h-10"
                              style={{ scale: 1.2 }}
                              ignoreScale={true}
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-bold truncate">{label}</div>
                            <div className="text-[10px] text-pk-gray-4 truncate">
                              #{p.display_id}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
              </>
            ) : (
              filteredCpu.map((name) => (
                <button
                  key={name}
                  onClick={() => setSlotPokemon(name)}
                  className="relative rounded-lg bg-pk-night-2/60 hover:bg-pk-night-2 border border-white/10 px-2 py-2 text-left text-white text-sm overflow-hidden"
                >
                  {/* Selected badge */}
                  {currentSlot?.base_pokemon_name === name && (
                    <div className="z-10 absolute top-1 right-1 rounded-md px-2 py-0.5 bg-pk-green-3 text-[10px] text-white font-semibold shadow-lg">
                      SELECTED
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <div className="w-12 h-12 rounded-md bg-black/20 border border-white/10 flex items-center justify-center">
                      <PokemonSprite
                        pokemon={spritePokemon(name)}
                        className="w-10 h-10"
                        style={{ scale: 1.2 }}
                        ignoreScale={true}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-bold truncate">{name}</div>
                      <div className="text-[10px] text-pk-gray-4 truncate">
                        Click to assign to slot
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Skills */}
        <div className="bg-black/20 border border-white/10 rounded-xl p-3">
          <div className="text-pk-gray-4 text-xs uppercase tracking-widest font-bold mb-2">
            Skills (pick up to 3)
          </div>
          {!currentSlot?.base_pokemon_name ? (
            <div className="text-pk-gray-4 text-sm italic">
              Select a pokemon to see skills
            </div>
          ) : (
            <div className="relative w-full">
              <div className="grid grid-cols-1 xs:grid-cols-2 gap-2 w-full max-h-[320px] overflow-y-auto">
                {knownSkills.map(
                  (skill) =>
                    skill && (
                      <PokemonSkillCard
                        key={skill.id}
                        skill={skill}
                        isSelected={currentSlot.active_skills.includes(
                          skill.id.replace(/_/g, "-"),
                        )}
                        onClick={() => toggleSkill(skill.id)}
                        isSelectable={true}
                      />
                    ),
                )}
              </div>
            </div>
          )}
        </div>

        <Button
          variant={canStart ? "red" : "night"}
          disabled={!canStart || isStarting}
          className="w-full py-4"
          onClick={handleStart}
        >
          <span className="font-black tracking-wider">
            {isStarting ? "STARTING..." : "START"}
          </span>
        </Button>
      </div>
    </div>
  );
};
